import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Image as ImageIcon, Eye, Loader2 } from "lucide-react";

interface GalleryImage {
  id: string;
  file_name: string;
  file_path: string;
  file_type: string;
  source: "upload" | "camera";
  ai_summary?: string | null;
  created_at: string;
}

interface GalleryImageDetailsProps {
  image: GalleryImage;
  onRefresh?: () => void;
}

export function GalleryImageDetails({ image, onRefresh }: GalleryImageDetailsProps) {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadImageUrl();
  }, [image.file_path]);

  async function loadImageUrl() {
    try {
      const { data, error } = await supabase.storage
        .from("patient-docs")
        .createSignedUrl(image.file_path, 3600);

      if (error) throw error;
      if (data?.signedUrl) {
        setImageUrl(data.signedUrl);
      }
    } catch (error) {
      console.error("Error loading image URL:", error);
    } finally {
      setLoading(false);
    }
  }

  const handleViewImage = () => {
    if (imageUrl) {
      window.open(imageUrl, '_blank');
    }
  };

  return (
    <div className="p-4 bg-muted/30 space-y-4">
      {/* Resumo IA */}
      {image.ai_summary && (
        <Card>
          <CardContent className="p-4 space-y-2">
            <h3 className="text-sm font-semibold text-muted-foreground mb-2">Resumo IA</h3>
            <p className="text-sm text-foreground leading-relaxed">{image.ai_summary}</p>
          </CardContent>
        </Card>
      )}

      {/* Preview da imagem */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-muted-foreground">Visualização</h3>
            <Button
              variant="outline"
              size="sm"
              onClick={handleViewImage}
              disabled={!imageUrl}
              className="gap-2"
            >
              <Eye className="h-4 w-4" />
              Abrir em Nova Aba
            </Button>
          </div>
          <div className="relative w-full bg-muted rounded-lg overflow-hidden border border-border/50">
            {loading ? (
              <div className="w-full h-64 flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : imageUrl ? (
              <img
                src={imageUrl}
                alt={image.file_name}
                className="w-full h-auto max-h-[500px] object-contain cursor-pointer hover:opacity-95 transition-opacity"
                onClick={handleViewImage}
                onError={(e) => {
                  console.error("Error loading image:", image.file_path);
                  e.currentTarget.style.display = "none";
                }}
              />
            ) : (
              <div className="w-full h-64 flex items-center justify-center">
                <ImageIcon className="h-12 w-12 text-muted-foreground" />
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

